/**
 * Canonical list of Indian states and union territories with capitals.
 * Sorted alphabetically by display name.
 */
export interface IndianStateInfo {
  /** Enum-style ID matching the API, e.g. "MAHARASHTRA" */
  id: string;
  /** Human-readable name, e.g. "Maharashtra" */
  name: string;
  /** Administrative capital / headquarters */
  capital: string;
}

export const INDIAN_STATES: IndianStateInfo[] = [
  { id: 'ANDAMAN_AND_NICOBAR', name: 'Andaman & Nicobar', capital: 'Port Blair' },
  { id: 'ANDHRA_PRADESH', name: 'Andhra Pradesh', capital: 'Amaravati' },
  { id: 'ARUNACHAL_PRADESH', name: 'Arunachal Pradesh', capital: 'Itanagar' },
  { id: 'ASSAM', name: 'Assam', capital: 'Dispur' },
  { id: 'BIHAR', name: 'Bihar', capital: 'Patna' },
  { id: 'CHANDIGARH', name: 'Chandigarh', capital: 'Chandigarh' },
  { id: 'CHHATTISGARH', name: 'Chhattisgarh', capital: 'Raipur' },
  { id: 'DADRA_AND_NAGAR_HAVELI_AND_DAMAN_AND_DIU', name: 'Dadra & Nagar Haveli and Daman & Diu', capital: 'Daman' },
  { id: 'DELHI', name: 'Delhi', capital: 'New Delhi' },
  { id: 'GOA', name: 'Goa', capital: 'Panaji' },
  { id: 'GUJARAT', name: 'Gujarat', capital: 'Gandhinagar' },
  { id: 'HARYANA', name: 'Haryana', capital: 'Chandigarh' },
  { id: 'HIMACHAL_PRADESH', name: 'Himachal Pradesh', capital: 'Shimla' },
  { id: 'JAMMU_AND_KASHMIR', name: 'Jammu & Kashmir', capital: 'Srinagar' },
  { id: 'JHARKHAND', name: 'Jharkhand', capital: 'Ranchi' },
  { id: 'KARNATAKA', name: 'Karnataka', capital: 'Bengaluru' },
  { id: 'KERALA', name: 'Kerala', capital: 'Thiruvananthapuram' },
  { id: 'LADAKH', name: 'Ladakh', capital: 'Leh' },
  { id: 'LAKSHADWEEP', name: 'Lakshadweep', capital: 'Kavaratti' },
  { id: 'MADHYA_PRADESH', name: 'Madhya Pradesh', capital: 'Bhopal' },
  { id: 'MAHARASHTRA', name: 'Maharashtra', capital: 'Mumbai' },
  { id: 'MANIPUR', name: 'Manipur', capital: 'Imphal' },
  { id: 'MEGHALAYA', name: 'Meghalaya', capital: 'Shillong' },
  { id: 'MIZORAM', name: 'Mizoram', capital: 'Aizawl' },
  { id: 'NAGALAND', name: 'Nagaland', capital: 'Kohima' },
  { id: 'ODISHA', name: 'Odisha', capital: 'Bhubaneswar' },
  { id: 'PUDUCHERRY', name: 'Puducherry', capital: 'Puducherry' },
  { id: 'PUNJAB', name: 'Punjab', capital: 'Chandigarh' },
  { id: 'RAJASTHAN', name: 'Rajasthan', capital: 'Jaipur' },
  { id: 'SIKKIM', name: 'Sikkim', capital: 'Gangtok' },
  { id: 'TAMIL_NADU', name: 'Tamil Nadu', capital: 'Chennai' },
  { id: 'TELANGANA', name: 'Telangana', capital: 'Hyderabad' },
  { id: 'TRIPURA', name: 'Tripura', capital: 'Agartala' },
  { id: 'UTTAR_PRADESH', name: 'Uttar Pradesh', capital: 'Lucknow' },
  { id: 'UTTARAKHAND', name: 'Uttarakhand', capital: 'Dehradun' },
  { id: 'WEST_BENGAL', name: 'West Bengal', capital: 'Kolkata' },
];

/** Look up a state info by its enum ID. */
export function findState(id: string): IndianStateInfo | undefined {
  return INDIAN_STATES.find((s) => s.id === id);
}

/** Format a state ID as "State Name (Capital)". */
export function formatStateWithCapital(id: string): string {
  const info = findState(id);
  if (!info) {
    return id.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return `${info.name} (${info.capital})`;
}

/**
 * Normalize a free-form address state ("Tamil Nadu", "jammu & kashmir") to an
 * IndianState enum ID, or null if it doesn't match any known state.
 * Superset of the backend IndianState.fromValue normalization (also maps "&" → "AND").
 */
export function normalizeStateId(value: string | null | undefined): string | null {
  if (!value) return null;
  const id = value
    .trim()
    .replace(/&/g, ' AND ')
    .toUpperCase()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
  return findState(id) ? id : null;
}

/** Well-known non-capital freight cities → state, for lane inference. */
const CITY_TO_STATE: Record<string, string> = {
  BANGALORE: 'KARNATAKA',
  MYSURU: 'KARNATAKA',
  MYSORE: 'KARNATAKA',
  MANGALURU: 'KARNATAKA',
  MANGALORE: 'KARNATAKA',
  BOMBAY: 'MAHARASHTRA',
  PUNE: 'MAHARASHTRA',
  NAGPUR: 'MAHARASHTRA',
  NASHIK: 'MAHARASHTRA',
  CALCUTTA: 'WEST_BENGAL',
  MADRAS: 'TAMIL_NADU',
  COIMBATORE: 'TAMIL_NADU',
  MADURAI: 'TAMIL_NADU',
  GURGAON: 'HARYANA',
  GURUGRAM: 'HARYANA',
  FARIDABAD: 'HARYANA',
  NOIDA: 'UTTAR_PRADESH',
  GHAZIABAD: 'UTTAR_PRADESH',
  KANPUR: 'UTTAR_PRADESH',
  AGRA: 'UTTAR_PRADESH',
  VARANASI: 'UTTAR_PRADESH',
  AHMEDABAD: 'GUJARAT',
  SURAT: 'GUJARAT',
  VADODARA: 'GUJARAT',
  RAJKOT: 'GUJARAT',
  KOCHI: 'KERALA',
  COCHIN: 'KERALA',
  KOZHIKODE: 'KERALA',
  INDORE: 'MADHYA_PRADESH',
  GWALIOR: 'MADHYA_PRADESH',
  LUDHIANA: 'PUNJAB',
  AMRITSAR: 'PUNJAB',
  JALANDHAR: 'PUNJAB',
  VISAKHAPATNAM: 'ANDHRA_PRADESH',
  VIJAYAWADA: 'ANDHRA_PRADESH',
  TIRUPATI: 'ANDHRA_PRADESH',
  JODHPUR: 'RAJASTHAN',
  UDAIPUR: 'RAJASTHAN',
  KOTA: 'RAJASTHAN',
  JAMSHEDPUR: 'JHARKHAND',
  DHANBAD: 'JHARKHAND',
  WARANGAL: 'TELANGANA',
  CUTTACK: 'ODISHA',
  GUWAHATI: 'ASSAM',
};

/** Split free text into normalized tokens ("Plot 1, Navi Mumbai" → [PLOT, NAVI, MUMBAI]). */
function tokensOf(value: string): string[] {
  return value
    .replace(/&/g, ' AND ')
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter(Boolean);
}

/** True when `seq` appears as consecutive tokens inside `tokens`. */
function containsSeq(tokens: string[], seq: string[]): boolean {
  outer: for (let i = 0; i + seq.length <= tokens.length; i++) {
    for (let j = 0; j < seq.length; j++) {
      if (tokens[i + j] !== seq[j]) continue outer;
    }
    return true;
  }
  return false;
}

// Capitals that map to exactly one state (excludes e.g. Chandigarh, shared by
// Punjab/Haryana/Chandigarh — the state-name pass resolves that one instead).
const UNIQUE_CAPITAL_SEQS: { seq: string[]; id: string }[] = (() => {
  const byCapital = new Map<string, string[]>();
  for (const s of INDIAN_STATES) {
    const key = tokensOf(s.capital).join('_');
    byCapital.set(key, [...(byCapital.get(key) ?? []), s.id]);
  }
  return [...byCapital.entries()]
    .filter(([, ids]) => ids.length === 1)
    .map(([key, ids]) => ({ seq: key.split('_'), id: ids[0] }));
})();

const STATE_NAME_SEQS: { seq: string[]; id: string }[] = INDIAN_STATES.map((s) => ({
  seq: s.id.split('_'),
  id: s.id,
}));

/**
 * Infer a state from free-form address text (city names, address lines).
 * Needed because shipment addresses store a literal "Unknown" state — the
 * backend never captures it (ShipmentService hardcodes it at creation).
 * Tries, across all given texts: explicit state name → unique capital city →
 * well-known major city. Returns null when nothing matches.
 */
export function inferStateId(...texts: (string | null | undefined)[]): string | null {
  const tokenLists = texts.filter((t): t is string => !!t).map(tokensOf);
  for (const candidates of [STATE_NAME_SEQS, UNIQUE_CAPITAL_SEQS]) {
    for (const tokens of tokenLists) {
      for (const { seq, id } of candidates) {
        if (containsSeq(tokens, seq)) return id;
      }
    }
  }
  for (const tokens of tokenLists) {
    for (const token of tokens) {
      const id = CITY_TO_STATE[token];
      if (id) return id;
    }
  }
  return null;
}

/** Format a state ID as just the display name (no capital). */
export function formatStateName(id: string): string {
  const info = findState(id);
  return info?.name ?? id.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
